import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Section, ArrowLink, Eyebrow, Swash } from "@/components/shared/PageHeader";
import { PhotoMosaic } from "@/components/shared/PhotoMosaic";
import { ArchiveDetails, ArchivePanorama, ZoomViewer } from "@/components/shared/ArchivePanorama";
import { CaptionedFigure } from "@/components/shared/CaptionedFigure";
import { HistoryLinks, Recordings } from "@/components/shared/HistoryExtras";
import { FadeUp } from "@/components/shared/FadeUp";
import { fetchContent, fetchSite } from "@/lib/api";
import { paragraphs } from "@/lib/format";
import { Seo } from "@/components/shared/Seo";
import { SIZES } from "@/components/shared/Img";

const ARCHIVE_ALT = "Historiske bilete frå Halsnøy og kulturhuset";
// The archive collage hangs on the wall in the foyer – the photograph of it closes the chapter.
const WALL_PHOTO = "/api/images/dfb3eb87-e698-4ee3-8af2-81c8a14200a9";

const Prose = ({ text }) => (
  <div className="prose-nn text-pretty">
    {paragraphs(text).map((p, i) => (
      <p key={i} className={i === 0 ? "font-display text-[clamp(22px,2vw,30px)] font-medium leading-[1.25] tracking-[-0.02em]" : "text-[17px] leading-relaxed text-foreground/85"}>{p}</p>
    ))}
  </div>
);

/** /historie – the story of the house as its own page: the archive collage, the text, close-ups, the wall in the foyer, and every photo the board has added. */
export default function History() {
  const { data: content, isLoading } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const hist = content?.historikk;
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const images = hist?.images || [];
  const captions = hist?.meta?.captions || {};
  const archive = images[0];
  const rest = images.slice(1);
  const body = paragraphs(hist?.body);
  const [intro, ...more] = body;
  const [view, setView] = useState(null);

  return (
    <div data-testid="history-page">
      <Seo title="Historie" description={(intro || "Historia til Halsnøy Kulturhus – bygda, huset og folket gjennom åra.").slice(0, 200)} image={archive} imageAlt={ARCHIVE_ALT} />
      <PageHeader
        eyebrow="Historie"
        title={<Swash text={hist?.title || "Huset, bygda og *folket* gjennom åra"} />}
        intro={intro}
      >
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 sm:mt-10">
          <ArrowLink href={`mailto:${email}?subject=Gamle%20bilete%20og%20historier%20fr%C3%A5%20huset`} up data-testid="history-share-link">Del bilete eller historier</ArrowLink>
          <ArrowLink to="/om-oss" data-testid="history-about-link">Om huset i dag</ArrowLink>
        </div>
      </PageHeader>

      {isLoading && !archive ? (
        <Skeleton className="h-[300px] w-full rounded-none sm:h-[360px] lg:h-[min(37.5vw,78vh,760px)]" data-testid="archive-panorama-skeleton" />
      ) : archive ? (
        <ArchivePanorama src={archive} alt={ARCHIVE_ALT} caption={captions[archive] || "Frå arkivet: bygda, huset og folket gjennom åra."} onOpen={() => setView({})} className="mt-2 sm:mt-4" />
      ) : null}

      <Section id="historia" eyebrow="Historia" className="pt-14 sm:pt-16 lg:pt-20">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <h2 className="t-h2 text-balance" data-testid="history-title">{hist?.title || "Historia til huset"}</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            {isLoading ? <Skeleton className="h-48 rounded-[12px]" /> : more.length ? <Prose text={more.join("\n\n")} /> : <p className="text-[17px] leading-relaxed text-muted-foreground">Meir av historia kjem – har du noko å bidra med, høyrer vi gjerne frå deg.</p>}
          </div>
        </div>
        {archive && (
          <FadeUp className="mt-12 sm:mt-14 lg:mt-16">
            <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
              <Eyebrow>Utsnitt frå arkivet</Eyebrow>
              <span className="t-mono-sm text-muted-foreground">Trykk på eit utsnitt for å sjå det nært</span>
            </div>
            <ArchiveDetails src={archive} onOpen={(d) => setView({ focus: d })} />
          </FadeUp>
        )}
        <FadeUp className="mt-12 grid gap-6 sm:mt-14 lg:grid-cols-12 lg:items-end lg:gap-10">
          <CaptionedFigure src={WALL_PHOTO} alt="Biletveggen med dei historiske fotografia i foajeen" width={1600} sizes={SIZES.twoThirds} imgClassName="aspect-[4/3] sm:aspect-[16/10]" className="lg:col-span-8" testId="history-wall-photo" />
          <div className="lg:col-span-4 lg:pb-2">
            <Eyebrow>I foajeen</Eyebrow>
            <p className="mt-4 font-display text-[clamp(20px,1.6vw,26px)] font-medium leading-[1.25] tracking-[-0.02em] text-balance">Bileta heng på veggen i foajeen – kom innom og sjå dei i full storleik.</p>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">Biletveggen er den første du møter når du kjem inn døra. Set deg ned i sofaen og finn att folk og stader frå øya.</p>
          </div>
        </FadeUp>
      </Section>

      {rest.length > 0 && (
        <Section id="bilete" eyebrow="Bilete" title={`${rest.length} bilete frå arkivet`} subtitle="Trykk på eit bilete for å sjå det stort. Har du fleire? Send dei til oss." tight>
          <PhotoMosaic images={rest} captions={captions} alt={hist?.title || "Historia til huset"} testId="history-gallery" />
        </Section>
      )}

      <Recordings videos={hist?.meta?.videos || []} />
      <HistoryLinks links={hist?.meta?.links || []} />

      <Section id="bidra" tight>
        <div className="flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-[17px] leading-relaxed text-muted-foreground">Har du gamle bilete, program eller historier frå huset og bygda? Vi tek vare på dei og viser dei her.</p>
          <Button asChild size="lg" data-testid="history-contribute-button"><a href={`mailto:${email}?subject=Gamle%20bilete%20og%20historier%20fr%C3%A5%20huset`}>Send oss bilete <ArrowUpRight className="h-4 w-4" /></a></Button>
        </div>
        <p className="mt-6 text-sm text-muted-foreground">Vil du vite korleis huset vert drive i dag? <Link to="/om-oss" className="u-link font-medium text-foreground">Les om huset, styret og dei faste brukarane</Link>.</p>
      </Section>

      <ZoomViewer src={archive} alt={ARCHIVE_ALT} open={Boolean(view)} focus={view?.focus} onOpenChange={(o) => { if (!o) setView(null); }} />
    </div>
  );
}
